import clsx, { type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { errorMessage } from '../lib/api';

/** Joins class names; later Tailwind classes override earlier ones (so `className` props always win). */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

// ------------------------------------------------------------------ buttons
type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'accent';
const variants: Record<Variant, string> = {
  primary: 'bg-brand-700 text-white hover:bg-brand-800 disabled:bg-brand-300',
  accent: 'bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50',
  secondary: 'bg-white text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50 disabled:text-slate-400',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300',
  ghost: 'text-slate-700 hover:bg-slate-100 disabled:text-slate-400',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed',
        // Small buttons still get a 44 px touch target on phones and tablets.
        size === 'sm' && 'h-11 px-3 text-sm lg:h-8',
        size === 'md' && 'h-11 px-4 text-sm lg:h-10',
        size === 'lg' && 'h-12 px-5 text-base',
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />}
      {children}
    </button>
  );
});

// ------------------------------------------------------------------ form fields
export function Field({ label, hint, error, children, className }: { label: string; hint?: ReactNode; error?: string | null; children: (id: string) => ReactNode; className?: string }) {
  const id = useId();
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      {children(id)}
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

const inputCls =
  'h-11 lg:h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-100';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cn(inputCls, className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(inputCls, 'h-auto min-h-20 py-2', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <select ref={ref} className={cn(inputCls, 'pr-8', className)} {...rest}>
      {children}
    </select>
  );
});

/** Rupee input that reports paise. */
export function MoneyInput({ valuePaise, onChange, ...rest }: { valuePaise: number | null; onChange: (paise: number | null) => void } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [text, setText] = useState(valuePaise == null ? '' : String(valuePaise / 100));
  useEffect(() => {
    const current = text === '' ? null : Math.round(Number(text) * 100);
    if (current !== valuePaise) setText(valuePaise == null ? '' : String(valuePaise / 100));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuePaise]);
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-500">₹</span>
      <Input
        inputMode="decimal"
        className="pl-7 tabular"
        value={text}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d.]/g, '');
          if (!/^\d*\.?\d{0,2}$/.test(v)) return;
          setText(v);
          onChange(v === '' || v === '.' ? null : Math.round(Number(v) * 100));
        }}
        {...rest}
      />
    </div>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  description,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  description?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-700" />
      <label htmlFor={id} className="text-sm">
        <span className="font-medium text-slate-800">{label}</span>
        {description && <span className="block text-slate-500">{description}</span>}
      </label>
    </div>
  );
}

// ------------------------------------------------------------------ layout pieces
export function Card({ children, className, title, actions }: { children: ReactNode; className?: string; title?: ReactNode; actions?: ReactNode }) {
  return (
    <section className={cn('rounded-xl border border-slate-200 bg-white shadow-sm', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
          {title && <h2 className="text-sm font-semibold text-slate-800">{title}</h2>}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'default' | 'good' | 'bad' }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={cn('mt-1 text-2xl font-semibold tabular', tone === 'good' && 'text-emerald-700', tone === 'bad' && 'text-red-700')}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

const badgeTones = {
  gray: 'bg-slate-100 text-slate-700',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  red: 'bg-red-50 text-red-700 ring-red-200',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  blue: 'bg-brand-50 text-brand-700 ring-brand-200',
};
export type Tone = keyof typeof badgeTones;

export function Badge({ children, tone = 'gray' }: { children: ReactNode; tone?: Tone }) {
  return <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ring-transparent', badgeTones[tone])}>{children}</span>;
}

const STATUS_TONES: Record<string, Tone> = {
  open: 'blue', exited: 'amber', closed: 'green', cancelled: 'gray',
  pending: 'amber', success: 'green', failed: 'red',
  issued: 'blue', redeemed: 'green', expired: 'gray', void: 'red',
  active: 'green', draft: 'gray', paused: 'amber', ended: 'gray', retired: 'gray',
  scheduled: 'blue', sending: 'amber', sent: 'green',
  validated: 'blue', committed: 'green', rolled_back: 'gray', discarded: 'gray', reopened: 'amber',
  reversed: 'gray', reversal: 'amber', blocked: 'red',
};
export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={STATUS_TONES[status] ?? 'gray'}>{status.replace(/_/g, ' ')}</Badge>;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center py-10" role="status" aria-label={label}>
      <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-100 border-t-brand-600" />
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center">
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {children && <div className="mt-1 text-sm text-slate-500">{children}</div>}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {errorMessage(error)}
    </div>
  );
}

export function Notice({ tone = 'blue', children }: { tone?: 'blue' | 'amber' | 'green'; children: ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2 text-sm',
        // Information is drawn in a neutral blue: the brand red is kept for errors (ErrorNote), so a note never reads as a fault.
        tone === 'blue' && 'border-sky-200 bg-sky-50 text-sky-900',
        tone === 'amber' && 'border-amber-200 bg-amber-50 text-amber-900',
        tone === 'green' && 'border-emerald-200 bg-emerald-50 text-emerald-800',
      )}
    >
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ table
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('relative -mx-4 overflow-x-auto', className)}>
      <table className="min-w-full divide-y divide-slate-200 text-sm">{children}</table>
    </div>
  );
}
export const Th = ({ children, className }: { children?: ReactNode; className?: string }) => (
  <th scope="col" className={cn('whitespace-nowrap px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500', className)}>
    {children}
  </th>
);
export const Td = ({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) => (
  <td colSpan={colSpan} className={cn('whitespace-nowrap px-4 py-2.5 text-slate-700', className)}>
    {children}
  </td>
);

/**
 * Phone-friendly alternative to a wide table: a plain list of cards on small screens, the table
 * (passed as `table`) from the `md` breakpoint up. Both receive the same rows, so nothing is lost.
 */
export function CardList<T>({ rows, keyOf, card, table, className }: { rows: T[]; keyOf: (row: T) => string; card: (row: T) => ReactNode; table?: ReactNode; className?: string }) {
  return (
    <>
      <ul className={cn('-mx-4 divide-y divide-slate-100', table && 'md:hidden', className)}>
        {rows.map((r) => (
          <li key={keyOf(r)} className="px-4 py-3">
            {card(r)}
          </li>
        ))}
      </ul>
      {table && <div className="hidden md:block">{table}</div>}
    </>
  );
}

/**
 * Keeps the main action under the thumb on a phone: sticks to the bottom of the screen while the
 * form above it scrolls. On larger screens it sits in the normal flow.
 */
export function BottomBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'sticky bottom-0 z-20 -mx-4 -mb-4 border-t border-slate-200 bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur',
        'lg:static lg:m-0 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ modal
export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // Its text wraps and reads left to right whatever table cell opened it (a cell may set nowrap / text-right).
      className={cn('m-auto w-[calc(100%-2rem)] rounded-xl p-0 text-left whitespace-normal shadow-xl backdrop:bg-slate-900/40', wide ? 'max-w-3xl' : 'max-w-lg')}
    >
      {open && (
        <div className="flex max-h-[85vh] flex-col">
          <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <h2 className="text-base font-semibold">{title}</h2>
            <button onClick={onClose} className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded text-slate-500 hover:bg-slate-100 lg:h-8 lg:w-8" aria-label="Close">
              ✕
            </button>
          </header>
          <div className="overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-5 py-3">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}

// ------------------------------------------------------------------ tabs
export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { value: T; label: ReactNode }[] }) {
  return (
    <div role="tablist" className="mb-4 flex gap-1 overflow-x-auto border-b border-slate-200">
      {tabs.map((t) => (
        <button
          key={t.value}
          role="tab"
          aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={cn(
            '-mb-px min-h-11 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium lg:min-h-0',
            value === t.value ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-800',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ toasts
type ToastItem = { id: number; message: string; tone: 'success' | 'error' };
const ToastCtx = createContext<{ push: (message: string, tone?: ToastItem['tone']) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, tone: ToastItem['tone'] = 'success') => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, message, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4">
        {items.map((t) => (
          <div key={t.id} className={cn('pointer-events-auto rounded-lg px-4 py-2 text-sm text-white shadow-lg', t.tone === 'success' ? 'bg-slate-900' : 'bg-red-600')}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return {
    success: (m: string) => ctx.push(m, 'success'),
    error: (e: unknown) => ctx.push(errorMessage(e), 'error'),
  };
}

// ------------------------------------------------------------------ misc
export function DateRange({ from, to, onChange }: { from: string; to: string; onChange: (r: { from: string; to: string }) => void }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Input type="date" aria-label="From date" value={from} max={to} onChange={(e) => onChange({ from: e.target.value, to })} className="w-auto min-w-0 flex-1 px-2 sm:flex-none" />
      <span className="text-slate-400">–</span>
      <Input type="date" aria-label="To date" value={to} min={from} onChange={(e) => onChange({ from, to: e.target.value })} className="w-auto min-w-0 flex-1 px-2 sm:flex-none" />
    </div>
  );
}

export function LoadMore({ hasMore, onClick, loading }: { hasMore?: boolean; onClick: () => void; loading?: boolean }) {
  if (!hasMore) return null;
  return (
    <div className="mt-3 flex justify-center">
      <Button variant="secondary" size="sm" onClick={onClick} loading={loading}>
        Load more
      </Button>
    </div>
  );
}
